/**
 * useChatBackend - Single owner of useQueryChatById for the active chat
 *
 * This hook centralizes backend data fetching for the chat system.
 * All components that need chat data should receive it from here via props,
 * rather than making their own useQueryChatById calls.
 *
 * Benefits:
 * - Single source of truth for backend chat data
 * - Consistent caching behavior
 * - Easier debugging and data flow tracing
 * - Eliminates query key divergence issues
 */

import { chatCapacityStatusAtomFamily } from '@/atoms/chatAtoms';
import { useQueryChatById } from '@/hooks/query/useQueryChatById';
import type { ChatResponse } from '@/types';
import { useSetAtom } from 'jotai';
import { useEffect, useMemo } from 'react';
import type { CapacityStatus, TimelineEntry } from '../types/types';
import { convertChatMessagesToTimeline } from '../utils/convertChatMessages';

/**
 * Metadata extracted from backend chat data.
 * This is what consumers need instead of raw ChatResponse.
 */
export interface ChatMeta {
  /** Chat title (user-generated or auto-generated) */
  title: string | undefined;
  /** Associated session ID (for in-session chats) */
  sessionId: string | undefined;
  /** Associated patient profile ID (for patient-linked chats) */
  patientProfileId: string | undefined;
  /** Last update timestamp as Unix ms */
  updatedAt: number | undefined;
  /** Whether the chat has any messages (for lock state) */
  hasMessages: boolean;
}

/**
 * Return type for useChatBackend hook.
 */
export interface UseChatBackendResult {
  /** Raw chat data from backend (use sparingly - prefer chatMeta/backendTimeline) */
  chatData: ChatResponse | undefined;
  /** Timeline entries converted from backend messages */
  backendTimeline: TimelineEntry[];
  /** Extracted metadata for common use cases */
  chatMeta: ChatMeta;
  /** Whether the query is fetching (true for any network request, even with cached data) */
  isFetching: boolean;
  /** Query error (if any) */
  error: Error | null;
}

/**
 * Extracts ChatMeta from a ChatResponse.
 */
function extractChatMeta(chatData: ChatResponse | undefined): ChatMeta {
  if (!chatData) {
    return {
      title: undefined,
      sessionId: undefined,
      patientProfileId: undefined,
      updatedAt: undefined,
      hasMessages: false,
    };
  }

  return {
    title: chatData.title,
    sessionId: chatData.session_id ?? undefined,
    patientProfileId: chatData.patient_profile_id ?? undefined,
    updatedAt: chatData.updated_at
      ? new Date(chatData.updated_at).getTime()
      : undefined,
    hasMessages: Boolean(chatData.messages && chatData.messages.length > 0),
  };
}

/**
 * Hook that owns the single useQueryChatById call for the active chat.
 *
 * Usage:
 * ```typescript
 * const { chatMeta, backendTimeline, isLoading, error } = useChatBackend(chatId);
 *
 * // Pass chatMeta to child components
 * <ChatHeader chatTitle={chatMeta.title} />
 *
 * // Use backendTimeline with buildLegacyTimeline
 * const result = buildLegacyTimeline({
 *   localTimeline,
 *   backendTimeline,
 *   isStreaming,
 * });
 * ```
 */
export function useChatBackend(
  chatId: string | null | undefined
): UseChatBackendResult {
  const { data: chatData, isFetching, error } = useQueryChatById(chatId);

  const setCapacityStatus = useSetAtom(
    chatCapacityStatusAtomFamily(chatId ?? '')
  );

  useEffect(() => {
    if (chatData?.capacity_status && chatId) {
      setCapacityStatus(chatData.capacity_status);
    }
  }, [chatData?.capacity_status, chatId, setCapacityStatus]);

  // Convert backend messages to timeline format
  // Memoized to prevent unnecessary conversions
  const backendTimeline = useMemo((): TimelineEntry[] => {
    if (!chatData?.messages || chatData.messages.length === 0) {
      return [];
    }
    return convertChatMessagesToTimeline(chatData.messages);
  }, [chatData?.messages]);

  // Extract metadata for consumers
  const chatMeta = useMemo((): ChatMeta => {
    return extractChatMeta(chatData);
  }, [chatData]);

  return {
    chatData,
    backendTimeline,
    chatMeta,
    isFetching,
    error: error ?? null,
  };
}
